<template>
<v-layout column>
    <v-form ref="editForm" v-model="formValid">
        <v-layout class="dialog-content" align-center justify-center>
            <v-flex xs12 md10 lg8>
                <v-layout wrap>
                    <v-flex xs12>
                        <v-toolbar dark flat color="primary">
                            <v-toolbar-title>
                                <span>Crop Rows</span>                                    
                            </v-toolbar-title>
                            <v-spacer></v-spacer>
                            <v-icon large dark>dehaze</v-icon>
                        </v-toolbar>
                    </v-flex>
                    <v-flex xs12>
                        <v-data-table :headers="headers" :items="crop.CropRows" :pagination.sync="pagination">
                            <template v-slot:headers="{ headers, indeterminate, all }">
                                <tr style="background:rgb(235, 237, 239)">
                                    <th
                                    v-for="header in headers"
                                    :key="header.text"
                                    :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                                    @click="changeSort(header.value)"
                                    >
                                    <v-icon small>arrow_upward</v-icon>
                                    {{ header.text }}
                                    </th>                                   
                                    <th>
                                        <v-btn icon color="primary" @click="addEditCropRow({ CropID: crop.CropID, PlantingDate: crop.PlantingDate })">
                                            <v-icon>add</v-icon>
                                        </v-btn>
                                    </th>
                                </tr>
                            </template>
                            <template v-slot:items="{ item }">
                                <td>{{ item.RowNumber }}</td>
                                <td>{{ lookupVarietyName(item.ProductVarietyID) }}</td>
                                <td>{{ formatDate(item.PlantingDate) }}</td>
                                <td>{{ item.NumberOfBushes }}</td>
                                <td>{{ (item.AreaSQM / 10000).toFixed(2) }}</td>
                                <td>
                                    <v-btn icon color="primary" @click="addEditCropRow(item)">
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                    <v-btn icon color="error" @click="handleDeleteItem(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </td>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-flex>
        </v-layout>
    </v-form>
    <v-layout row align-center justify-center>
        <v-flex xs12 md10 lg8>
            <v-layout wrap>
                    <v-flex xs12>
                        <v-toolbar dark color="grey" flat dense height="45px">
                            <v-toolbar-title class="body-2 white--text">
                                <span>Summary</span>                                    
                            </v-toolbar-title>                            
                        </v-toolbar>
                    </v-flex>
                    <v-flex xs12>
                        <v-data-table :headers="summaryHeaders" :items="cropRowSummary" hide-actions>
                            <template v-slot:items="{ item }">
                                <td>{{ lookupVarietyName(item.ProductVarietyID) }}</td>
                                <td>{{ item.Rows }}</td>
                                <td>{{ item.Bushes }}</td>
                                <td>{{ (item.AreaSQM / 10000).toFixed(2) }}</td>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
        </v-flex>
    </v-layout>
    <v-layout row align-center justify-center>
        <v-flex xs12 md10 lg8>
            <v-layout row>
                <v-flex xs4 pa-2>
                    <v-btn color="error" block @click="$emit('cancel')">Cancel</v-btn>
                </v-flex>
                <v-flex xs4 pa-2>
                    <v-btn color="primary" block @click="$emit('previous')" :loading="submitLoading">Previous</v-btn>
                </v-flex>
                <v-flex xs4 pa-2>
                    <v-btn color="primary" :disabled="!formValid" block @click="next" :loading="submitLoading">Next</v-btn>
                </v-flex>                
            </v-layout>
        </v-flex>
    </v-layout>
    <v-alert :value="submitError != null" outline type="error">
        <span>{{ submitError }}</span>
    </v-alert>
    <delete-dialog :DisplayDialog="showConfirmDelete" :DialogTitle="'Delete Item'" :DialogContents="'Are you sure you wish to delete this entry?'" @No="showConfirmDelete = false" @Yes="confirmDelete"></delete-dialog>
    <v-dialog 
        :value="showEditCropRow"
        max-width="500px" 
        persistent
        @keydown.esc="handleCropRowCancel"
        >
        <crop-row-edit 
            v-if="showEditCropRow"
            :productTypeID="crop.ProductTypeID"
            :item="cropRowEditData"
            :submitError="error"
            @submit="handleCropRowSubmit"
            @cancel="handleCropRowCancel">
        </crop-row-edit>
    </v-dialog>
</v-layout>
</template>

<script>
import CropRowEdit from '@/components/crops/CropRowEdit.vue';
import DeleteDialog from '@/components/generic/YesNoPopup.vue';
import UtilService from '@/services/utilService.js';

export default {
    components: {
        CropRowEdit: CropRowEdit,
        DeleteDialog: DeleteDialog
    },
    props: {
        crop: {
            required: true,
            type: Object
        },
        submitError: {
            required: false,
            type: String
        },
        submitLoading: {
            required: false,
            type: Boolean
        }
    },
    data() {
        return {
            formValid: true,            
            cropRowEditData: null,
            showEditCropRow: false,
            error: null,
            deleteItem: null,
            showConfirmDelete: false,
            pagination: {
                sortBy: 'RowNumber'
            },
            headers: [
                {
                    text: "Row Name",
                    value: "RowNumber",
                    sortable: true
                },
                {
                    text: "Variety",
                    value: "ProductVarietyID",
                    sortable: true
                },
                {
                    text: "Planting Date",
                    value: "PlantingDate",
                    sortable: true
                },
                {
                    text: "Num. of Bushes",
                    value: "NumberOfBushes",
                    sortable: true
                },
                {
                    text: "Area (ha)",
                    value: "AreaSQM",
                    sortable: true
                }
            ],
            summaryHeaders: [                
                {
                    text: "Variety",
                    value: "ProductVarietyName",
                    sortable: false
                },
                {
                    text: "Rows",
                    value: "Rows",
                    sortable: false
                },
                {
                    text: "Bushes",
                    value: "Bushes",
                    sortable: false
                },
                {
                    text: "Area (ha)",
                    value: "AreaSQM",
                    sortable: false
                }
            ]
        }
    },
    computed: {
        cropRowSummary() {
            let summary = [];

            this.crop.CropRows.forEach(row => {
                let match = summary.find(s => s.ProductVarietyID === row.ProductVarietyID);

                if (!match) {
                    match = {
                        ProductVarietyID: row.ProductVarietyID,
                        Rows: 0,
                        Bushes: 0,
                        AreaSQM: 0
                    }

                    summary.push(match);
                }

                match.Rows += 1;
                match.Bushes += parseFloat(row.NumberOfBushes);
                match.AreaSQM += parseFloat(row.AreaSQM)
            });

            return summary
        },
        ProductVarieties() {
            return this.$store.getters["CropStore/getProductVarieties"](this.crop.ProductTypeID)
        }
    },
    methods: {
        async next() {
            this.$emit('next');
        },
        changeSort (column) {
            if (this.pagination.sortBy === column) {
                this.pagination.descending = !this.pagination.descending
            } else {
                this.pagination.sortBy = column
                this.pagination.descending = false
            }
        },
        async addEditCropRow(cropRow) {
            if (!cropRow.CropRowID) {
                let newID = -1;
                newID -= this.crop.CropRows.filter(c => c.CropRowID < 0).length;

                cropRow.CropRowID = newID;
            }

            this.cropRowEditData = cropRow;
            this.showEditCropRow = true;
        },
        async handleCropRowSubmit(item) {
            if (this.crop.CropRows.filter(a => a.RowNumber.trim() === item.RowNumber.trim() && a.CropRowID !== item.CropRowID).length > 0) {
                this.error = "Crop row with the same name already exists."
                return;
            }
            
            this.error = null;
            this.showEditCropRow = false;

            let index = this.crop.CropRows.findIndex(a => a.CropRowID === item.CropRowID);            

            if (index >= 0) {
                this.crop.CropRows.splice(index, 1, item);
            } else {
                this.crop.CropRows.push(item);
            }
        },
        async handleCropRowCancel() {
            this.showEditCropRow = false;
        },
        formatDate(v) {
            if (v) {
                return UtilService.formatDateDMY(v, '/');
            }

            return null;
        },
        handleDeleteItem(item) {
            this.deleteItem = item;
            this.showConfirmDelete = true;
        },
        confirmDelete() {
            const index = this.crop.CropRows.indexOf(this.deleteItem);
            this.crop.CropRows.splice(index, 1)            
            this.showConfirmDelete = false;
        },
        lookupVarietyName(productVarietyID) {
            let match = this.ProductVarieties.find(p => p.ProductVarietyID === productVarietyID);

            if (match) {
                return match.Name;
            }

            return 'Unknown';
        }
    }
}
</script>
